// Navigation par profil. Chaque entrée indique le lot de construction qui livre son module : une entrée
// dont le module n'est pas encore construit n'est jamais affichée (aucun bouton sans traitement). Les lots
// sont construits en parallèle (architecture, section 13) : une entrée d'un lot non entièrement construit est
// affichée quand son module figure dans MODULES_CONSTRUITS.

import type { Action, Ressource, Role } from './constantes';

/** Dernier lot dont tous les modules sont construits dans cette version du logiciel. */
export const LOT_CONSTRUIT = 3;

/** Modules des lots suivants déjà construits et intégrés (codes de la matrice de couverture). */
export const MODULES_CONSTRUITS: readonly string[] = ['H2', 'G2', 'G3', 'G5', 'H6'];

/** État de construction : dernier lot complet et modules des lots suivants déjà livrés. */
export interface Construction {
  lot: number;
  modules: readonly string[];
}

export type Profil =
  | 'ministere'
  | 'administration'
  | 'distributeur'
  | 'station'
  | 'conducteur'
  | 'inspecteur'
  | 'operateur'
  | 'technique'
  | 'citoyen';

export interface EntreeNavigation {
  code: string;
  libelle: string;
  chemin: string;
  lot: number;
  /** Permission minimale requise ; l'entrée est masquée sans elle. */
  exige?: { action: Action; ressource: Ressource };
  /** Entrée proposée dans la barre inférieure sur téléphone. */
  mobile?: boolean;
  /** Module livré par l'entrée (code de la matrice), pour un lot construit en partie. */
  module?: string;
  /** Code d'une entrée qui regroupe celle-ci : quand elle est disponible, celle-ci n'est pas répétée. */
  regroupeDans?: string;
}

const ACCUEIL: EntreeNavigation = { code: 'accueil', libelle: 'Situation', chemin: '/', lot: 1, mobile: true };
const REFERENTIEL: EntreeNavigation = {
  code: 'referentiel',
  libelle: 'Référentiel',
  chemin: '/referentiel/installations',
  lot: 1,
  exige: { action: 'lire', ressource: 'installations' },
  mobile: true,
};
const CATALOGUE: EntreeNavigation = {
  code: 'catalogue',
  libelle: 'Catalogue',
  chemin: '/catalogue',
  lot: 1,
  exige: { action: 'lire', ressource: 'catalogue' },
};
const DOCUMENTS: EntreeNavigation = {
  code: 'documents',
  libelle: 'Documents',
  chemin: '/documents',
  lot: 1,
  exige: { action: 'lire', ressource: 'documents' },
};
// Lot 6 (contrôle) : pôle Contrôle de la DGH et du ministère, qui regroupe inspections (G2), incidents (G3),
// prix et observations (G5) quand il est disponible ; incidents proposés sur téléphone aux acteurs de terrain.
const CONTROLE: EntreeNavigation = {
  code: 'controle',
  libelle: 'Contrôle',
  chemin: '/controle',
  lot: 6,
  module: 'G2',
  exige: { action: 'lire', ressource: 'inspections' },
  mobile: true,
};
const INSPECTIONS: EntreeNavigation = {
  code: 'inspections',
  libelle: 'Inspections',
  chemin: '/inspections',
  lot: 6,
  module: 'G2',
  exige: { action: 'lire', ressource: 'inspections' },
  mobile: true,
  regroupeDans: 'controle',
};
const INCIDENTS: EntreeNavigation = {
  code: 'incidents',
  libelle: 'Incidents',
  chemin: '/incidents',
  lot: 6,
  module: 'G3',
  exige: { action: 'lire', ressource: 'incidents' },
  mobile: true,
  regroupeDans: 'controle',
};
const PRIX: EntreeNavigation = {
  code: 'prix',
  libelle: 'Prix',
  chemin: '/prix',
  lot: 6,
  module: 'G5',
  exige: { action: 'lire', ressource: 'prix' },
  regroupeDans: 'controle',
};
const INTEGRATIONS: EntreeNavigation = {
  code: 'integrations',
  libelle: 'Intégrations',
  chemin: '/integrations',
  lot: 6,
  module: 'H6',
  exige: { action: 'lire', ressource: 'integrations' },
};
const ADMINISTRATION: EntreeNavigation = {
  code: 'administration',
  libelle: 'Administration',
  chemin: '/administration/organisations',
  lot: 1,
  exige: { action: 'lire', ressource: 'organisations' },
};

const CONFLITS: EntreeNavigation = {
  code: 'conflits',
  libelle: 'Conflits',
  chemin: '/synchronisation/conflits',
  lot: 3,
  exige: { action: 'valider', ressource: 'synchronisation' },
};
const LIVRAISONS: EntreeNavigation = { code: 'livraisons', libelle: 'Livraisons', chemin: '/livraisons', lot: 3, exige: { action: 'lire', ressource: 'ordres_transport' } };
const VENTES: EntreeNavigation = { code: 'ventes', libelle: 'Ventes', chemin: '/ventes', lot: 3, exige: { action: 'creer', ressource: 'ventes' } };
const RAPPROCHEMENTS: EntreeNavigation = { code: 'rapprochements', libelle: 'Rapprochements', chemin: '/rapprochements', lot: 3, exige: { action: 'lire', ressource: 'rapprochements' } };

export const NAVIGATION_PAR_PROFIL: Record<Profil, EntreeNavigation[]> = {
  ministere: [
    ACCUEIL,
    CONTROLE,
    { code: 'stocks', libelle: 'Stocks', chemin: '/stocks', lot: 2, exige: { action: 'lire', ressource: 'agregats' } },
    { code: 'approvisionnements', libelle: 'Approvisionnements', chemin: '/approvisionnements', lot: 2, exige: { action: 'lire', ressource: 'agregats' } },
    { code: 'carte', libelle: 'Carte', chemin: '/carte', lot: 4 },
    { code: 'alertes', libelle: 'Alertes', chemin: '/alertes', lot: 2, exige: { action: 'lire', ressource: 'alertes' } },
    LIVRAISONS,
    INCIDENTS,
    PRIX,
    { code: 'previsions', libelle: 'Prévisions', chemin: '/previsions', lot: 5 },
    { code: 'crise', libelle: 'Crise', chemin: '/crise', lot: 5 },
    { code: 'rapports', libelle: 'Rapports', chemin: '/rapports', lot: 4 },
    REFERENTIEL,
    DOCUMENTS,
  ],
  administration: [
    ACCUEIL,
    CONTROLE,
    { code: 'stocks', libelle: 'Stocks', chemin: '/stocks', lot: 2, exige: { action: 'lire', ressource: 'agregats' } },
    { code: 'approvisionnements', libelle: 'Approvisionnements', chemin: '/approvisionnements', lot: 2, exige: { action: 'lire', ressource: 'agregats' } },
    { code: 'alertes', libelle: 'Alertes', chemin: '/alertes', lot: 2, exige: { action: 'lire', ressource: 'alertes' } },
    LIVRAISONS,
    RAPPROCHEMENTS,
    INCIDENTS,
    PRIX,
    CONFLITS,
    REFERENTIEL,
    CATALOGUE,
    DOCUMENTS,
    ADMINISTRATION,
    INTEGRATIONS,
  ],
  distributeur: [
    { ...ACCUEIL, libelle: 'Mon réseau' },
    { code: 'stocks', libelle: 'Mes stocks', chemin: '/stocks', lot: 2, exige: { action: 'lire', ressource: 'positions' } },
    { code: 'commandes', libelle: 'Commandes', chemin: '/commandes', lot: 3, exige: { action: 'lire', ressource: 'besoins' } },
    { code: 'planification', libelle: 'Planification', chemin: '/planification', lot: 3, exige: { action: 'creer', ressource: 'ordres_transport' } },
    { ...LIVRAISONS, mobile: true },
    RAPPROCHEMENTS,
    VENTES,
    INCIDENTS,
    {
      code: 'transporteurs',
      libelle: 'Transporteurs',
      chemin: '/referentiel/organisations?type=transporteur',
      lot: 1,
      exige: { action: 'lire', ressource: 'organisations' },
    },
    {
      code: 'stations',
      libelle: 'Stations',
      chemin: '/referentiel/installations?categorie=station',
      lot: 1,
      exige: { action: 'lire', ressource: 'installations' },
      mobile: true,
    },
    { code: 'rapports', libelle: 'Rapports', chemin: '/rapports', lot: 4 },
    DOCUMENTS,
    CONFLITS,
    { code: 'parametres', libelle: 'Paramètres', chemin: '/preferences', lot: 1 },
  ],
  station: [
    { ...ACCUEIL, libelle: 'Accueil' },
    { code: 'stocks', libelle: 'Mes cuves', chemin: '/stocks', lot: 2, exige: { action: 'lire', ressource: 'stocks' }, mobile: true },
    { code: 'declarer', libelle: 'Déclarer', chemin: '/declarer', lot: 3, exige: { action: 'creer', ressource: 'stocks' }, mobile: true },
    { ...LIVRAISONS, mobile: true },
    INCIDENTS,
    VENTES,
    { code: 'historique', libelle: 'Historique', chemin: '/station/historique', lot: 3, exige: { action: 'lire', ressource: 'stations' } },
    { code: 'messages', libelle: 'Messages', chemin: '/messages', lot: 5, module: 'H2', mobile: true },
    { ...DOCUMENTS, mobile: true },
  ],
  conducteur: [
    { ...ACCUEIL, libelle: 'Accueil' },
    { code: 'missions', libelle: 'Missions', chemin: '/missions', lot: 3, exige: { action: 'lire', ressource: 'ordres_transport' }, mobile: true },
    { code: 'flotte', libelle: 'Flotte', chemin: '/flotte', lot: 3, exige: { action: 'lire', ressource: 'ordres_transport' }, mobile: true },
    INCIDENTS,
    { code: 'messages', libelle: 'Messages', chemin: '/messages', lot: 5, module: 'H2', mobile: true },
    DOCUMENTS,
  ],
  inspecteur: [
    { ...ACCUEIL, libelle: 'Accueil' },
    INSPECTIONS,
    INCIDENTS,
    PRIX,
    { code: 'alertes', libelle: 'Alertes', chemin: '/alertes', lot: 2, exige: { action: 'lire', ressource: 'alertes' }, mobile: true },
    REFERENTIEL,
    DOCUMENTS,
  ],
  operateur: [
    ACCUEIL,
    { code: 'stocks', libelle: 'Stocks', chemin: '/stocks', lot: 2, exige: { action: 'lire', ressource: 'stocks' }, mobile: true },
    { code: 'approvisionnements', libelle: 'Approvisionnements', chemin: '/approvisionnements', lot: 2, exige: { action: 'lire', ressource: 'approvisionnements' } },
    { code: 'gpl', libelle: 'GPL', chemin: '/gpl', lot: 2, exige: { action: 'lire', ressource: 'gpl' } },
    { code: 'chargements', libelle: 'Chargements', chemin: '/chargements', lot: 3, exige: { action: 'valider', ressource: 'ordres_transport' }, mobile: true },
    LIVRAISONS,
    RAPPROCHEMENTS,
    { code: 'alertes', libelle: 'Alertes', chemin: '/alertes', lot: 2, exige: { action: 'lire', ressource: 'alertes' }, mobile: true },
    INCIDENTS,
    REFERENTIEL,
    DOCUMENTS,
    CONFLITS,
  ],
  technique: [
    { ...ACCUEIL, libelle: 'Exploitation' },
    { code: 'journaux', libelle: 'Journaux techniques', chemin: '/exploitation/journaux', lot: 6 },
  ],
  citoyen: [{ ...ACCUEIL, libelle: 'Accueil' }],
};

const PROFIL_PAR_ROLE: Record<Role, Profil> = {
  administrateur_national: 'administration',
  ministre_cabinet: 'ministere',
  secretaire_general: 'ministere',
  directeur_general: 'ministere',
  directeur: 'ministere',
  chef_service: 'ministere',
  superviseur: 'ministere',
  agent_administratif: 'administration',
  agent_terrain: 'inspecteur',
  inspecteur: 'inspecteur',
  depot: 'operateur',
  distributeur: 'distributeur',
  transporteur: 'conducteur',
  station: 'station',
  etablissement_prioritaire: 'operateur',
  caistab: 'ministere',
  externe_habilite: 'operateur',
  support_technique: 'technique',
  administrateur_idg: 'technique',
  autorite_provinciale: 'ministere',
  citoyen: 'citoyen',
};

/** Profil retenu pour un ensemble de rôles : le premier rôle dans l'ordre des affectations. */
export function profilPourRoles(roles: readonly Role[]): Profil {
  const premier = roles[0];
  return premier ? PROFIL_PAR_ROLE[premier] : 'citoyen';
}

/** Vrai si le module de l'entrée est construit (lot complet ou module déjà livré d'un lot suivant). */
export function entreeConstruite(e: EntreeNavigation, construit: Construction): boolean {
  return e.lot <= construit.lot || (e.module !== undefined && construit.modules.includes(e.module));
}

/**
 * Entrées disponibles pour un profil : module construit, permission présente, et entrée non regroupée dans une
 * autre entrée disponible. Un nombre en troisième argument désigne le dernier lot complet, sans module au-delà.
 */
export function navigationDisponible(
  profil: Profil,
  autorise: (action: Action, ressource: Ressource) => boolean,
  construit: number | Construction = { lot: LOT_CONSTRUIT, modules: MODULES_CONSTRUITS },
): EntreeNavigation[] {
  const etat = typeof construit === 'number' ? { lot: construit, modules: [] } : construit;
  const possibles = NAVIGATION_PAR_PROFIL[profil].filter(
    (e) => entreeConstruite(e, etat) && (!e.exige || autorise(e.exige.action, e.exige.ressource)),
  );
  const codes = new Set(possibles.map((e) => e.code));
  return possibles.filter((e) => !e.regroupeDans || !codes.has(e.regroupeDans));
}
